import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { getUserSettings, updateUserSettings } from '../apis/APICalls';
import { useApiCall } from '../hooks/useApiCall';
import { useAuth } from './AuthContext';

// Define default settings
const defaultSettings = {
    setting_spread: 2.0,
    setting_time_frame: '1min',
    setting_periods_back: 50,
    setting_periods_forward: 50,
    setting_take_profit: 50.0,
    setting_stop_loss: 50.0,
    // Chart context toggles (defaults disabled)
    setting_show_timeframe_label: false,
    setting_show_price_scale: false,
    setting_show_time_scale: false,
    setting_show_current_price_line: false,
    setting_show_sl_tp_lines: false
};

// Create Context
const SettingsContext = createContext();

// Create Provider Component
export const SettingsProvider = ({ children }) => {
    const [settings, setSettings] = useState(defaultSettings);
    const [isLoading, setIsLoading] = useState(false);
    const { executeApiCall } = useApiCall();
    const { isAuthenticated } = useAuth();

    // Imperative loader to fetch settings on demand
    const loadSettings = useCallback(async () => {
        if (!isAuthenticated) {
            // Still allow defaults for anonymous users
        }
        setIsLoading(true);
        return executeApiCall(() => getUserSettings(), {
            onSuccess: (loadedSettings) => {
                setSettings(prev => ({ ...prev, ...loadedSettings }));
                setIsLoading(false);
            },
            onError: () => {
                console.error("Failed to load user settings, using defaults.");
                setIsLoading(false);
            }
        });
    }, [executeApiCall, isAuthenticated]);

    // Auto-load within a session only if session has started elsewhere
    useEffect(() => {
        try {
            const started = sessionStorage.getItem('tv_session_started') === 'true';
            if (started) {
                loadSettings();
            }
        } catch (e) {}
    }, [loadSettings]);

    // Function to update a specific setting
    const updateSetting = useCallback((key, value) => {
        setSettings(prev => {
            const newSettings = { ...prev, [key]: value };
            // Save updated settings to backend
            const payload = { [key]: value };
            executeApiCall(() => updateUserSettings(payload), {
                onSuccess: (savedSettings) => {},
                onError: (err) => {
                    console.error("Failed to save setting:", err);
                }
            });
            return newSettings;
        });
    }, [executeApiCall]);

    // Convenience: mark tutorial completed in settings
    const markTutorialCompleted = useCallback(() => {
        updateSetting('tutorial_completed', true);
        try { localStorage.setItem('tv_tutorial_completed', 'true'); } catch (e) {}
    }, [updateSetting]);

    return (
        <SettingsContext.Provider value={{ settings, updateSetting, isLoading, markTutorialCompleted, loadSettings }}>
            {children}
        </SettingsContext.Provider>
    );
};

// Custom hook to use the settings context
export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (context === undefined) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};

// --- Remove or comment out the old individual hooks --- 
/*
export const useTimeFrame = () => { ... };
export const useSpread = () => { ... };
// ... and so on for other hooks
*/ 